import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Certificate, BookOpen, ArrowRight } from '@phosphor-icons/react';
import Navbar from '../components/Navbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CertificationCenter() {
  const [certifications, setCertifications] = useState([]);
  const [selectedState, setSelectedState] = useState('PA');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchCertifications = useCallback(async () => {
    try {
      let url = `${API}/certifications?`;
      const params = [];
      if (selectedState) params.push(`state=${selectedState}`);
      if (selectedCategory) params.push(`category=${selectedCategory}`);
      
      const { data } = await axios.get(url + params.join('&'));
      setCertifications(data);
    } catch (error) {
      console.error('Error fetching certifications:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedState, selectedCategory]);

  useEffect(() => {
    fetchCertifications();
  }, [fetchCertifications]);

  const states = ['PA', 'OH', 'VA'];
  const categories = [
    'Core',
    'Household/Health',
    'Wood Destroying',
    'Public Health Vertebrate',
    'Public Health Invertebrate',
    'Park/School',
    'Wildlife'
  ];

  return (
    <div className="min-h-screen bg-background_light">
      <Navbar />
      <div className="bg-primary text-text_light py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Certificate size={48} weight="duotone" />
            <h1 className="text-5xl sm:text-6xl tracking-tight font-semibold">Certification Study Center</h1>
          </div>
          <p className="text-xl leading-relaxed text-secondary max-w-4xl">
            State-by-state licensing study resources, exam prep, and official links for pest control certification.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="bg-white border border-borders rounded-sm p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block mb-2 font-medium text-sm uppercase tracking-[0.2em]">State</label>
              <select
                data-testid="state-filter"
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="w-full px-4 py-3 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All States</option>
                {states.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block mb-2 font-medium text-sm uppercase tracking-[0.2em]">Category</label>
              <select
                data-testid="category-filter"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-3 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="mb-12">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium mb-6">Official Resources</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white border border-borders rounded-sm p-6">
              <h3 className="text-xl font-medium mb-3">Pennsylvania</h3>
              <ul className="space-y-2 text-base leading-relaxed">
                <li>
                  <a href="https://www.paplants.pa.gov" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline flex items-center gap-2">
                    <ArrowRight size={16} />
                    PA Plants
                  </a>
                </li>
                <li>
                  <a href="https://pested.psu.edu" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline flex items-center gap-2">
                    <ArrowRight size={16} />
                    Penn State Pesticide Education
                  </a>
                </li>
              </ul>
            </div>
            <div className="bg-white border border-borders rounded-sm p-6">
              <h3 className="text-xl font-medium mb-3">Ohio</h3>
              <ul className="space-y-2 text-base leading-relaxed">
                <li>
                  <a href="https://agri.ohio.gov" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline flex items-center gap-2">
                    <ArrowRight size={16} />
                    Ohio Dept of Agriculture
                  </a>
                </li>
              </ul>
            </div>
            <div className="bg-white border border-borders rounded-sm p-6">
              <h3 className="text-xl font-medium mb-3">Virginia</h3>
              <ul className="space-y-2 text-base leading-relaxed">
                <li>
                  <a href="https://www.vdacs.virginia.gov" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline flex items-center gap-2">
                    <ArrowRight size={16} />
                    Virginia DACS
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium mb-6">
            Study Materials
          </h2>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
            </div>
          ) : certifications.length === 0 ? (
            <div className="bg-white border border-borders rounded-sm p-8 text-center">
              <p className="text-gray-600">Study materials coming soon. Check official state resources in the meantime.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {certifications.map(cert => (
                <div key={cert.id} className="bg-white border border-borders rounded-sm p-6" data-testid={`cert-${cert.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-2xl tracking-tight font-medium mb-2">{cert.title}</h3>
                      <div className="flex gap-4 text-sm text-gray-600">
                        <span className="px-3 py-1 bg-gray-100 rounded-sm">{cert.state}</span>
                        <span className="px-3 py-1 bg-gray-100 rounded-sm">{cert.category}</span>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-base leading-relaxed mb-4 text-gray-700">{cert.description}</p>
                  
                  {cert.resources.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-medium text-sm uppercase tracking-[0.2em] mb-2">Resources</h4>
                      <ul className="list-disc list-inside space-y-1 text-base leading-relaxed">
                        {cert.resources.map((resource, idx) => (
                          <li key={resource}>{resource}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {cert.official_links.length > 0 && (
                    <div>
                      <h4 className="font-medium text-sm uppercase tracking-[0.2em] mb-2">Official Links</h4>
                      <div className="flex flex-wrap gap-2">
                        {cert.official_links.map((link, idx) => (
                          <a
                            key={idx}
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-accent hover:underline text-sm flex items-center gap-1"
                          >
                            <ArrowRight size={16} />
                            Link {idx + 1}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}